import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AppShell from "../components/AppShell";
import { KioskSessionCard } from "../components/KioskSessionCard";
import StatCard from "../components/StatCard";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { useAuth } from "../context/AuthContext";
import api, { downloadFile } from "../lib/api";
import { getAttendanceFeatureLabel, getEnabledAttendanceOptions } from "../lib/attendance";
import { extractTime, formatDate, formatDateTime, formatHours, formatMinutes, getErrorMessage } from "../lib/formatters";


const navItems = [
  { path: "/company/dashboard", label: "Company Dashboard", shortLabel: "HOME", testId: "company-dashboard" },
  { path: "/company/employees", label: "Employees", shortLabel: "PEOPLE", testId: "company-employees" },
  { path: "/company/attendance", label: "Attendance Monitor", shortLabel: "TRACK", testId: "company-attendance" },
  { path: "/company/approvals", label: "Approvals", shortLabel: "QUEUE", testId: "company-approvals" },
  { path: "/company/kiosk", label: "Kiosk QR", shortLabel: "QR", testId: "company-kiosk" },
  { path: "/company/leave-settings", label: "Leave Settings", shortLabel: "LEAVE", testId: "company-leave-settings" },
  { path: "/company/reports", label: "Reports", shortLabel: "REPORTS", testId: "company-reports" },
  { path: "/company/settings", label: "Settings", shortLabel: "CONFIG", testId: "company-settings" },
];


const localDateValue = () => new Date().toLocaleDateString("en-CA");
const timeValue = (isoValue) => (isoValue ? new Date(isoValue).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false }) : "");
const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});


export default function CompanyAdminPage({ section }) {
  const navigate = useNavigate();
  const { user, tenant, featureFlags, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [referenceData, setReferenceData] = useState({ employees: [], departments: [], teams: [], shifts: [], locations: [], business_units: [], scanner_admins: [] });
  const [monitorData, setMonitorData] = useState(null);
  const [approvals, setApprovals] = useState({ leave_requests: [], regularization_requests: [] });
  const [sessions, setSessions] = useState([]);
  const [settingsData, setSettingsData] = useState(null);
  const [leaveSetup, setLeaveSetup] = useState({ settings: null, leave_types: [], leave_enabled: false });
  const [reportData, setReportData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(localDateValue());
  const [resettingEmployeeId, setResettingEmployeeId] = useState("");
  const [openingSessionId, setOpeningSessionId] = useState("");
  const [savingEmployeeId, setSavingEmployeeId] = useState("");
  const [assignmentDrafts, setAssignmentDrafts] = useState({});
  const [selectedEmployeeForLeave, setSelectedEmployeeForLeave] = useState(null);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [employeeFilters, setEmployeeFilters] = useState({ department: "", location: "", attendance: "", status: "", lifecycle: "active" });
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkFileName, setBulkFileName] = useState("");
  const [bulkValidation, setBulkValidation] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkBusy, setBulkBusy] = useState("");
  const [passwordResetResult, setPasswordResetResult] = useState(null);
  const [employeeForm, setEmployeeForm] = useState({
    name: "",
    email: "",
    phone: "",
    role: "employee",
    employee_code: "",
    department_id: "",
    team_id: "",
    shift_id: "",
    location_id: "",
    business_unit_id: "",
    aadhar_number: "",
    profile_picture_data: "",
    manager_user_id: "",
    assigned_attendance_feature: "",
    leave_assignments: [],
  });
  const [scannerAdminForm, setScannerAdminForm] = useState({ name: "", email: "", phone: "", location_id: "" });
  const [sessionForm, setSessionForm] = useState({ label: "Front Desk Kiosk", expires_minutes: 10 });
  const [correctionDrafts, setCorrectionDrafts] = useState({});
  const [reportFilters, setReportFilters] = useState({ period: "monthly", anchor_date: localDateValue(), department_id: "", employee_id: "" });
  const [policyForm, setPolicyForm] = useState({
    block_attendance_on_approved_leave: true,
    allow_early_clock_in: true,
    allow_attendance_fallback: false,
    allow_photo_scanner_without_profile_picture: false,
    photo_scanner_local_approve_threshold: 0.78,
    photo_scanner_local_fallback_threshold: 0.52,
    photo_scanner_gemini_approve_threshold: 0.82,
    photo_scanner_gemini_daily_soft_limit: 60,
    weekly_off_days: [6],
  });
  const [leaveSettingsForm, setLeaveSettingsForm] = useState({ enabled: false, leave_year_type: "calendar_year", credit_method: "manual", carry_forward_enabled: false, carry_forward_limit: 0 });
  const [leaveTypeForm, setLeaveTypeForm] = useState({ code: "", name: "", monthly_credit: 0, active: true });
  const [settingsForms, setSettingsForms] = useState({
    departmentName: "",
    teamName: "",
    teamDepartmentId: "",
    shiftName: "",
    shiftType: "fixed",
    shiftStart: "09:30",
    shiftEnd: "18:30",
    shiftGrace: 10,
    shiftHalfDay: 240,
    shiftRequired: 480,
    locationName: "",
    businessUnitName: "",
    holidayName: "",
    holidayDate: localDateValue(),
  });

  const pageMeta = useMemo(
    () => ({
      dashboard: { title: "Operational command centre", subtitle: "Track present, absent, late, leave, overtime, and approvals for your company in one place." },
      employees: { title: "Employee directory", subtitle: "Manage staff, scanner admins, attendance assignment, locations, and business-unit mapping for your tenant." },
      attendance: { title: "Daily attendance monitor", subtitle: "Review live attendance rows, late flags, and bulk correction actions with tenant-safe controls." },
      approvals: { title: "Approval workflow queue", subtitle: "Approve or reject leave requests and regularization submissions without leaving the browser." },
      kiosk: { title: "Shared kiosk QR sessions", subtitle: "Generate session-bound QR codes with expiry and tenant validation for browser-based attendance." },
      "leave-settings": { title: "Leave settings", subtitle: "Define leave year, crediting, carry forward, and company-specific leave types only when your company wants to use leave management." },
      reports: { title: "Attendance exports", subtitle: "Filter daily, weekly, and monthly data and export Excel-ready reports for operations and finance." },
      settings: { title: "Company policies & setup", subtitle: "Configure holidays, attendance policy, locations, business units, departments, teams, and shifts for your tenant." },
    }),
    [],
  );

  const employeeMap = useMemo(
    () => Object.fromEntries(referenceData.employees.map((employee) => [employee.id, employee])),
    [referenceData.employees],
  );

  const managerOptions = referenceData.employees.filter((employee) => employee.role === "manager");
  const attendanceOptions = useMemo(() => getEnabledAttendanceOptions(featureFlags), [featureFlags]);
  const requiresAttendanceAssignment = attendanceOptions.length > 1;
  const defaultAttendanceAssignment = attendanceOptions.length === 1 ? attendanceOptions[0].value : "";
  const leaveTypeOptions = leaveSetup.leave_types.filter((item) => item.active !== false);
  const filteredEmployees = useMemo(
    () => referenceData.employees.filter((employee) => {
      const matchesSearch = !employeeSearch || `${employee.name} ${employee.employee_code} ${employee.email}`.toLowerCase().includes(employeeSearch.toLowerCase());
      const matchesDepartment = !employeeFilters.department || employee.today_snapshot?.department_name === employeeFilters.department;
      const matchesLocation = !employeeFilters.location || employee.location_id === employeeFilters.location;
      const matchesAttendance = !employeeFilters.attendance || employee.assigned_attendance_feature === employeeFilters.attendance;
      const matchesStatus = !employeeFilters.status || (employee.today_snapshot?.current_status || "Absent") === employeeFilters.status;
      const employeeLifecycle = employee.status || (employee.active === false ? "inactive" : "active");
      const matchesLifecycle = employeeFilters.lifecycle === "all" || employeeLifecycle === employeeFilters.lifecycle;
      return matchesSearch && matchesDepartment && matchesLocation && matchesAttendance && matchesStatus && matchesLifecycle;
    }),
    [referenceData.employees, employeeFilters, employeeSearch],
  );

  const loadSectionData = async () => {
    setLoading(true);
    try {
      if (section === "dashboard") {
        const response = await api.get("/company/dashboard");
        setDashboard(response.data);
      }
      if (section === "employees") {
        const [employees, departments, teams, shifts, leaveSettingsResponse, locations, businessUnits, scannerAdmins] = await Promise.all([api.get("/employees"), api.get("/departments"), api.get("/teams"), api.get("/shifts"), api.get("/company/leave-settings"), api.get("/locations"), api.get("/business-units"), api.get("/scanner-admins")]);
        setReferenceData({ employees: employees.data, departments: departments.data, teams: teams.data, shifts: shifts.data, locations: locations.data, business_units: businessUnits.data, scanner_admins: scannerAdmins.data });
        setLeaveSetup(leaveSettingsResponse.data);
        setAssignmentDrafts(Object.fromEntries(employees.data.map((employee) => [employee.id, employee.assigned_attendance_feature || defaultAttendanceAssignment])));
        setEmployeeForm((current) => ({
          ...current,
          department_id: departments.data[0]?.id || "",
          team_id: teams.data[0]?.id || "",
          shift_id: shifts.data[0]?.id || "",
          location_id: locations.data[0]?.id || "",
          business_unit_id: businessUnits.data[0]?.id || "",
          manager_user_id: teams.data[0]?.id ? current.manager_user_id : "",
          assigned_attendance_feature: attendanceOptions.some((option) => option.value === current.assigned_attendance_feature) ? current.assigned_attendance_feature : defaultAttendanceAssignment,
          leave_assignments: leaveSettingsResponse.data.leave_enabled ? current.leave_assignments : [],
        }));
        setScannerAdminForm((current) => ({ ...current, location_id: locations.data[0]?.id || current.location_id }));
      }
      if (section === "attendance") {
        const response = await api.get("/attendance/monitor", { params: { target_date: selectedDate } });
        setMonitorData(response.data);
      }
      if (section === "approvals") {
        const [queue, employees] = await Promise.all([api.get("/approvals/queue"), api.get("/employees")]);
        setApprovals(queue.data);
        setReferenceData((current) => ({ ...current, employees: employees.data }));
      }
      if (section === "kiosk") {
        if (!featureFlags?.kiosk_qr_attendance) {
          setSessions([]);
        } else {
          const response = await api.get("/kiosk/sessions");
          setSessions(response.data);
        }
      }
      if (section === "reports") {
        const [report, employees, departments] = await Promise.all([
          api.get("/reports/summary", { params: reportFilters }),
          api.get("/employees"),
          api.get("/departments"),
        ]);
        setReportData(report.data);
        setReferenceData((current) => ({ ...current, employees: employees.data, departments: departments.data }));
      }
      if (section === "settings") {
        const response = await api.get("/company/settings");
        setSettingsData(response.data);
        setLeaveSetup({ settings: response.data.leave_settings, leave_types: response.data.leave_types, leave_enabled: response.data.leave_enabled });
        setPolicyForm({
          block_attendance_on_approved_leave: response.data.policy.block_attendance_on_approved_leave,
          allow_early_clock_in: response.data.policy.allow_early_clock_in,
          allow_attendance_fallback: response.data.policy.allow_attendance_fallback,
          allow_photo_scanner_without_profile_picture: response.data.policy.allow_photo_scanner_without_profile_picture,
          photo_scanner_local_approve_threshold: response.data.policy.photo_scanner_local_approve_threshold ?? 0.78,
          photo_scanner_local_fallback_threshold: response.data.policy.photo_scanner_local_fallback_threshold ?? 0.52,
          photo_scanner_gemini_approve_threshold: response.data.policy.photo_scanner_gemini_approve_threshold ?? 0.82,
          photo_scanner_gemini_daily_soft_limit: response.data.policy.photo_scanner_gemini_daily_soft_limit ?? 60,
          weekly_off_days: Array.isArray(response.data.policy.weekly_off_days) ? response.data.policy.weekly_off_days : [6],
        });
        setSettingsForms((current) => ({
          ...current,
          teamDepartmentId: response.data.departments[0]?.id || "",
        }));
      }
      if (section === "leave-settings") {
        const response = await api.get("/company/leave-settings");
        setLeaveSetup(response.data);
        setLeaveSettingsForm(response.data.settings);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadSectionData();
  }, [section, selectedDate, attendanceOptions, defaultAttendanceAssignment]);

  useEffect(() => {
    if (section !== "kiosk" || !featureFlags?.kiosk_qr_attendance) return undefined;
    const intervalId = window.setInterval(async () => {
      try {
        const response = await api.get("/kiosk/sessions");
        setSessions(response.data);
      } catch {
        // background refresh intentionally silent
      }
    }, 5000);
    return () => window.clearInterval(intervalId);
  }, [section, featureFlags?.kiosk_qr_attendance]);

  const handleCreateEmployee = async (event) => {
    event.preventDefault();
    const payload = {
      ...employeeForm,
      assigned_attendance_feature: employeeForm.assigned_attendance_feature || defaultAttendanceAssignment || undefined,
    };
    if (requiresAttendanceAssignment && !payload.assigned_attendance_feature) {
      toast.error("Please assign one attendance method before saving this employee");
      return;
    }
    if (leaveSetup.leave_enabled && !payload.leave_assignments.length) {
      toast.error("Please assign leave type and opening balance before saving this employee");
      return;
    }
    try {
      await api.post("/employees", payload);
      toast.success("Employee created successfully");
      setEmployeeForm((current) => ({ ...current, name: "", email: "", phone: "", employee_code: "", aadhar_number: "", profile_picture_data: "", manager_user_id: "", assigned_attendance_feature: defaultAttendanceAssignment, leave_assignments: [] }));
      setShowEmployeeModal(false);
      loadSectionData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleEmployeeProfileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await fileToDataUrl(file);
      setEmployeeForm((current) => ({ ...current, profile_picture_data: dataUrl }));
      toast.success("Profile picture attached");
    } catch {
      toast.error("Could not read the profile picture")
    }
  };

  const handleCreateScannerAdmin = async (event) => {
    event.preventDefault();
    try {
      const response = await api.post("/scanner-admins", scannerAdminForm);
      toast.success("Scanner Admin created");
      if (response.data.temporary_password) {
        toast.success(`Temporary password: ${response.data.temporary_password}`);
      }
      setScannerAdminForm((current) => ({ ...current, name: "", email: "", phone: "" }));
      loadSectionData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleUpdateScannerAdmin = async (scannerAdmin, updates) => {
    try {
      await api.put(`/scanner-admins/${scannerAdmin.id}`, updates);
      toast.success("Scanner Admin updated");
      loadSectionData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleCreateLocation = async () => {
    if (!settingsForms.locationName.trim()) return;
    try {
      await api.post("/locations", { name: settingsForms.locationName });
      toast.success("Location created");
      setSettingsForms((current) => ({ ...current, locationName: "" }));
      loadSectionData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleRenameLocation = async (location) => {
    const name = window.prompt("Rename location", location.name);
    if (!name || name === location.name) return;
    try {
      await api.put(`/locations/${location.id}`, { name });
      toast.success("Location updated");
      loadSectionData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleDeleteLocation = async (location) => {
    try {
      await api.delete(`/locations/${location.id}`);
      toast.success("Location deleted");
      loadSectionData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleCreateBusinessUnit = async () => {
    if (!settingsForms.businessUnitName.trim()) return;
    try {
      await api.post("/business-units", { name: settingsForms.businessUnitName });
      toast.success("Business unit created");
      setSettingsForms((current) => ({ ...current, businessUnitName: "" }));
      loadSectionData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleRenameBusinessUnit = async (businessUnit) => {
    const name = window.prompt("Rename business unit", businessUnit.name);
    if (!name || name === businessUnit.name) return;
    try {
      await api.put(`/business-units/${businessUnit.id}`, { name });
      toast.success("Business unit updated");
      loadSectionData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleDeleteBusinessUnit = async (businessUnit) => {
    try {
      await api.delete(`/business-units/${businessUnit.id}`);
      toast.success("Business unit deleted");
      loadSectionData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const toggleLeaveAssignment = (code, checked, currentAssignments, setter) => {
    if (checked) {
      setter((current) => ({ ...current, leave_assignments: [...currentAssignments, { leave_type_code: code, opening_balance: 0 }] }));
      return;
    }
    setter((current) => ({ ...current, leave_assignments: currentAssignments.filter((item) => item.leave_type_code !== code) }));
  };

  const updateLeaveAssignmentBalance = (code, value, currentAssignments, setter) => {
    setter((current) => ({
      ...current,
      leave_assignments: currentAssignments.map((item) => (item.leave_type_code === code ? { ...item, opening_balance: Number(value) } : item)),
    }));
  };

  const handleSaveEmployeeAssignment = async (employee) => {
    const assignedFeature = assignmentDrafts[employee.id] || defaultAttendanceAssignment;
    if (requiresAttendanceAssignment && !assignedFeature) {
      toast.error("Please select an attendance method before saving");
      return;
    }
    setSavingEmployeeId(employee.id);
    try {
      await api.put(`/employees/${employee.id}`, { assigned_attendance_feature: assignedFeature });
      toast.success(`${employee.name}'s attendance method updated`);
      loadSectionData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSavingEmployeeId("");
    }
  };

  const handleSelectEmployeeLeave = (employee) => {
    setSelectedEmployeeForLeave({
      ...employee,
      leave_assignments: (employee.leave_assignments || []).map((item) => ({ leave_type_code: item.leave_type_code, opening_balance: item.opening_balance ?? item.total ?? 0 })),
    });
  };

  const handleSaveEmployeeLeaveAssignments = async () => {
    if (!selectedEmployeeForLeave) return;
    if (leaveSetup.leave_enabled && !selectedEmployeeForLeave.leave_assignments.length) {
      toast.error("Please assign at least one leave type");
      return;
    }
    try {
      await api.put(`/employees/${selectedEmployeeForLeave.id}/leave-assignments`, { leave_assignments: selectedEmployeeForLeave.leave_assignments });
      toast.success("Employee leave assignments updated");
      setSelectedEmployeeForLeave(null);
      loadSectionData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleLeaveDecision = async (requestId, action) => {
    try {
      await api.put(`/leaves/requests/${requestId}/decision`, { action });
      toast.success(`Leave request ${action}`);
      loadSectionData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleRegularizationDecision = async (requestId, action) => {
    try {
      await api.put(`/attendance/regularizations/${requestId}/decision`, { action });
      toast.success(`Regularization ${action}`);
      loadSectionData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleCorrection = async (recordId) => {
    const draft = correctionDrafts[recordId];
    if (!draft) return;
    try {
      await api.post("/attendance/bulk-correction", { corrections: [{ record_id: recordId, ...draft }] });
      toast.success("Attendance corrected");
      loadSectionData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleCreateSession = async (event) => {
    event.preventDefault();
    try {
      await api.post("/kiosk/sessions", sessionForm);
      toast.success("Kiosk QR session created. It will rotate every minute.");
      loadSectionData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleResetEmployeePassword = async (employee) => {
    setResettingEmployeeId(employee.id);
    try {
      const response = await api.post(`/employees/${employee.id}/reset-password`, { send_email: false });
      setPasswordResetResult({ employeeName: employee.name, temporaryPassword: response.data.temporary_password });
      toast.success("Temporary password generated");
      loadSectionData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setResettingEmployeeId("");
    }
  };

  const handleOpenKioskWindow = async (session) => {
    const popup = window.open("", "_blank", "width=1320,height=920");
    setOpeningSessionId(session.id);
    try {
      const response = await api.post(`/kiosk/sessions/${session.id}/open-window`);
      const liveUrl = `${window.location.origin}${response.data.live_path || `/kiosk/live/${session.id}?viewer_token=${response.data.viewer_token}`}`;
      if (popup) {
        popup.location.replace(liveUrl);
        popup.focus();
      } else {
        window.open(liveUrl, "_blank", "width=1320,height=920");
      }
      toast.success("Live kiosk window opened");
      const latestSession = await api.get(`/kiosk/sessions/${session.id}/state`);
      setSessions((current) => current.map((item) => (item.id === session.id ? latestSession.data : item)));
    } catch (error) {
      if (popup) popup.close();
      toast.error(getErrorMessage(error));
    } finally {
      setOpeningSessionId("");
    }
  };

  const handleSaveLeaveSettings = async () => {
    try {
      const response = await api.put("/company/leave-settings", leaveSettingsForm);
      setLeaveSetup(response.data);
      setLeaveSettingsForm(response.data.settings);
      toast.success("Leave settings saved");
      loadSectionData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleCreateLeaveType = async () => {
    try {
      await api.post("/company/leave-types", leaveTypeForm);
      toast.success("Leave type created");
      setLeaveTypeForm({ code: "", name: "", monthly_credit: 0, active: true });
      const response = await api.get("/company/leave-settings");
      setLeaveSetup(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleToggleLeaveType = async (leaveType) => {
    try {
      await api.put(`/company/leave-types/${leaveType.id}`, { active: !leaveType.active });
      const response = await api.get("/company/leave-settings");
      setLeaveSetup(response.data);
      toast.success("Leave type updated");
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const loadReports = async () => {
    try {
      const response = await api.get("/reports/summary", { params: reportFilters });
      setReportData(response.data);
      toast.success("Report refreshed");
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const exportReports = async () => {
    try {
      await downloadFile("/reports/export", reportFilters);
      toast.success("Excel report downloaded");
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleSettingsAction = async (type) => {
    try {
      if (type === "policy") {
        await api.put("/company/policy", policyForm);
      }
      if (type === "department") {
        await api.post("/departments", { name: settingsForms.departmentName });
        setSettingsForms((current) => ({ ...current, departmentName: "" }));
      }
      if (type === "team") {
        await api.post("/teams", { name: settingsForms.teamName, department_id: settingsForms.teamDepartmentId });
        setSettingsForms((current) => ({ ...current, teamName: "" }));
      }
      if (type === "shift") {
        await api.post("/shifts", {
          name: settingsForms.shiftName,
          type: settingsForms.shiftType,
          start_time: settingsForms.shiftStart,
          end_time: settingsForms.shiftEnd,
          grace_minutes: Number(settingsForms.shiftGrace),
          half_day_minutes: Number(settingsForms.shiftHalfDay),
          required_minutes: Number(settingsForms.shiftRequired),
        });
        setSettingsForms((current) => ({ ...current, shiftName: "" }));
      }
      if (type === "holiday") {
        await api.post("/holidays", { name: settingsForms.holidayName, date: settingsForms.holidayDate });
        setSettingsForms((current) => ({ ...current, holidayName: "" }));
      }
      toast.success("Settings updated");
      loadSectionData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const renderDashboard = () => (
    <>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-6" data-testid="company-dashboard-stats-grid">
        <StatCard label="Employees" value={dashboard?.stats?.total_employees || 0} accent="#0D1B2A" helper="Active employees in tenant" testId="company-stat-employees" />
        <StatCard label="Present" value={dashboard?.stats?.present || 0} accent="#F4A300" helper="Clocked in or clocked out today" testId="company-stat-present" />
        <StatCard label="Absent" value={dashboard?.stats?.absent || 0} accent="#EF4444" helper="No attendance logged today" testId="company-stat-absent" />
        <StatCard label="Late" value={dashboard?.stats?.late || 0} accent="#F59E0B" helper="Late arrivals after grace period" testId="company-stat-late" />
        <StatCard label="Leave" value={dashboard?.stats?.leave || 0} accent="#0D1B2A" helper="Approved leave for today" testId="company-stat-leave" />
        <StatCard label="Pending" value={dashboard?.stats?.pending_approvals || 0} accent="#0D1B2A" helper="Approvals waiting in queue" testId="company-stat-pending" />
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
        <Card className="border-slate-200/70 bg-white/85" data-testid="company-live-monitor-card">
          <CardHeader>
            <CardTitle data-testid="company-live-monitor-title">Today’s company roster</CardTitle>
            <CardDescription data-testid="company-live-monitor-description">A table-first view of in/out status, lateness, and overtime by employee.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-hidden rounded-3xl border border-slate-200/70" data-testid="company-live-monitor-table-wrap">
              <Table data-testid="company-live-monitor-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Hours</TableHead>
                    <TableHead>Late</TableHead>
                    <TableHead>OT</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(dashboard?.attendance_rows || []).map((row) => (
                    <TableRow key={row.employee_id} data-testid={`company-dashboard-row-${row.employee_id}`}>
                      <TableCell>
                        <div>
                          <p className="font-semibold text-slate-900">{row.employee_name}</p>
                          <p className="text-xs text-slate-500">{row.employee_code}</p>
                        </div>
                      </TableCell>
                      <TableCell><StatusBadge value={row.current_status} testId={`company-status-${row.employee_id}`} /></TableCell>
                      <TableCell>{row.department_name}</TableCell>
                      <TableCell>{formatHours(row.total_hours)}</TableCell>
                      <TableCell>{formatMinutes(row.late_minutes)}</TableCell>
                      <TableCell>{formatMinutes(row.overtime_minutes)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200/70 bg-white/85" data-testid="company-feature-flags-card">
          <CardHeader>
            <CardTitle data-testid="company-feature-flags-title">Attendance methods in this plan</CardTitle>
            <CardDescription data-testid="company-feature-flags-description">Feature flags are enforced in both UI and backend routes.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {[
              ["web_portal_attendance", "Web Portal Attendance"],
              ["kiosk_qr_attendance", "Kiosk QR Attendance"],
              ["employee_qr_scan_attendance", "Employee QR Scan"],
              ["photo_scanner_attendance", "Photo Scanner Attendance"],
            ].map(([key, label]) => (
              <div key={key} className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid={`company-flag-${key}`}>
                <div>
                  <p className="font-semibold text-slate-900">{label}</p>
                  <p className="text-sm text-slate-500">Controlled by Super Admin at platform level.</p>
                </div>
                <StatusBadge value={featureFlags?.[key] ? "Present" : "Absent"} testId={`company-flag-badge-${key}`} />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </>
  );

  const resetBulkImport = () => {
    setBulkFile(null);
    setBulkFileName("");
    setBulkValidation(null);
    setBulkResult(null);
    setBulkBusy("");
  };

  const handleBulkTemplateDownload = async () => {
    try {
      await downloadFile("/employees/bulk/template");
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleBulkFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const dataUrl = await fileToDataUrl(file);
    setBulkFile(dataUrl);
    setBulkFileName(file.name);
    setBulkValidation(null);
    setBulkResult(null);
  };

  const handleBulkValidate = async () => {
    if (!bulkFile) return;
    setBulkBusy("validate");
    try {
      const response = await api.post("/employees/bulk/validate", { file_base64: bulkFile });
      setBulkValidation(response.data);
      if (response.data.valid) toast.success("Validation successful. Ready to create employees.");
      else toast.error(`${response.data.errors.length} validation error(s) found.`);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setBulkBusy("");
    }
  };

  const handleBulkCreate = async () => {
    if (!bulkFile) return;
    setBulkBusy("create");
    try {
      const response = await api.post("/employees/bulk/create", { file_base64: bulkFile });
      setBulkResult(response.data);
      toast.success(`${response.data.employees_created} employees created.`);
      await loadSectionData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setBulkBusy("");
    }
  };

  const handleBulkErrorReport = async () => {
    try {
      const response = await api.post("/employees/bulk/error-report", { errors: bulkValidation?.errors || [] }, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", "tarka-hr-import-errors.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const bulkStep = bulkResult ? 4 : bulkValidation?.valid ? 3 : bulkFile ? 2 : 1;

  const renderBulkModal = () => (
    <Dialog open={showBulkModal} onOpenChange={(open) => { setShowBulkModal(open); if (!open) resetBulkImport(); }}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto rounded-[28px] border border-[#0D1B2A]/10 bg-white p-0" data-testid="bulk-import-modal">
        <DialogHeader className="border-b border-slate-200 px-6 py-5">
          <DialogTitle data-testid="bulk-import-title">Bulk Import Employees</DialogTitle>
          <DialogDescription data-testid="bulk-import-description">Download the template, fill employee details, upload, then validate & create.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 px-6 py-6">
          <div className="grid grid-cols-4 gap-2" data-testid="bulk-import-steps">
            {["Download Template", "Fill Details", "Upload File", "Validate & Create"].map((label, index) => (
              <div key={label} className={`rounded-2xl border px-3 py-2 text-center text-xs font-semibold ${bulkStep >= index + 1 ? "border-[#F4A300]/30 bg-[#F4A300]/10 text-[#C27D00]" : "border-slate-200 bg-white text-slate-400"}`}>
                <span className="block text-[11px]">Step {index + 1}</span>{label}
              </div>
            ))}
          </div>

          {bulkResult ? (
            <div className="space-y-4" data-testid="bulk-import-result">
              <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-5">
                <p className="text-lg font-bold text-emerald-800">Bulk Employee Import Complete</p>
                <div className="mt-3 grid gap-2 text-sm text-emerald-900 md:grid-cols-2">
                  <p>Total rows: <span className="font-semibold">{bulkResult.total_rows}</span></p>
                  <p>Employees created: <span className="font-semibold" data-testid="bulk-result-created">{bulkResult.employees_created}</span></p>
                  <p>Imported by: <span className="font-semibold">{bulkResult.imported_by}</span></p>
                  <p>Company: <span className="font-semibold">{bulkResult.tenant_name}</span></p>
                  <p className="md:col-span-2">Import time: <span className="font-semibold">{formatDateTime(bulkResult.imported_at)}</span></p>
                </div>
              </div>
              {bulkResult.warnings?.length ? (
                <div className="rounded-3xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="bulk-result-warnings">
                  <p className="font-semibold">Warnings</p>
                  <ul className="mt-2 list-disc space-y-1 pl-5">
                    {bulkResult.warnings.map((warning, index) => <li key={index}>Row {warning.row}: {warning.message}</li>)}
                  </ul>
                </div>
              ) : null}
              <div className="flex flex-wrap justify-end gap-3">
                <Button type="button" variant="outline" className="rounded-2xl" onClick={() => { setShowBulkModal(false); resetBulkImport(); }} data-testid="bulk-view-directory-button">View Employee Directory</Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3 rounded-3xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-900">1. Download the official Excel template</p>
                  <p className="text-xs text-slate-500">Includes an Instructions sheet. Mandatory columns are marked with *.</p>
                </div>
                <Button type="button" variant="outline" className="rounded-2xl" onClick={handleBulkTemplateDownload} data-testid="bulk-download-template-button">Download Template</Button>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm font-semibold text-slate-900">2. Fill details & 3. Upload the completed file</p>
                <Input type="file" accept=".xlsx" className="mt-3 bg-white" onChange={handleBulkFileChange} data-testid="bulk-upload-input" />
                {bulkFileName ? <p className="mt-2 text-xs font-semibold text-emerald-700" data-testid="bulk-file-name">{bulkFileName}</p> : null}
              </div>

              {bulkValidation && !bulkValidation.valid ? (
                <div className="rounded-3xl border border-rose-200 bg-rose-50 p-4" data-testid="bulk-error-list">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-rose-800">{bulkValidation.errors.length} validation error(s). No employees created.</p>
                    <Button type="button" variant="outline" className="rounded-2xl border-rose-300 text-rose-700" onClick={handleBulkErrorReport} data-testid="bulk-download-errors-button">Download Error Report</Button>
                  </div>
                  <div className="mt-3 max-h-56 overflow-y-auto rounded-2xl border border-rose-100 bg-white">
                    <table className="w-full text-left text-xs">
                      <thead><tr className="bg-rose-50 text-rose-700"><th className="px-3 py-2">Row</th><th className="px-3 py-2">Employee</th><th className="px-3 py-2">Field</th><th className="px-3 py-2">Error</th></tr></thead>
                      <tbody>
                        {bulkValidation.errors.map((error, index) => (
                          <tr key={index} className="border-t border-rose-100" data-testid={`bulk-error-row-${index}`}>
                            <td className="px-3 py-2 font-semibold">{error.row}</td>
                            <td className="px-3 py-2">{error.employee_name || "—"}</td>
                            <td className="px-3 py-2">{error.field}</td>
                            <td className="px-3 py-2 text-rose-700">{error.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}

              {bulkValidation?.valid ? (
                <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-4" data-testid="bulk-validation-success">
                  <p className="text-sm font-semibold text-emerald-800">Validation successful. Ready to create {bulkValidation.total_rows} employee(s).</p>
                  {bulkValidation.warnings?.length ? <p className="mt-2 text-xs text-amber-700">{bulkValidation.warnings.length} warning(s) — photo scanner employees missing a profile picture.</p> : null}
                </div>
              ) : null}

              <div className="flex flex-wrap justify-end gap-3">
                <Button type="button" variant="outline" className="rounded-2xl" onClick={() => { setShowBulkModal(false); resetBulkImport(); }} data-testid="bulk-cancel-button">Cancel</Button>
                {!bulkValidation?.valid ? (
                  <Button type="button" className="rounded-2xl bg-[#0D1B2A] text-white hover:bg-[#1a2c40]" disabled={!bulkFile || bulkBusy === "validate"} onClick={handleBulkValidate} data-testid="bulk-validate-button">{bulkBusy === "validate" ? "Validating…" : "Validate File"}</Button>
                ) : (
                  <Button type="button" className="rounded-2xl bg-[#F4A300] text-[#0D1B2A] hover:bg-[#D89000]" disabled={bulkBusy === "create"} onClick={handleBulkCreate} data-testid="bulk-create-button">{bulkBusy === "create" ? "Creating…" : "Create Employees"}</Button>
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );

  const renderEmployees = () => (
    <>
      <Card className="border-slate-200/70 bg-white/85" data-testid="employee-create-card">
        <CardHeader>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <CardTitle data-testid="employee-create-title">People setup</CardTitle>
              <CardDescription data-testid="employee-create-description">Create employees in a cleaner modal flow, then manage scanner admins separately for location-based photo attendance.</CardDescription>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button type="button" variant="outline" className="rounded-2xl border-[#0D1B2A]/20" onClick={() => setShowBulkModal(true)} data-testid="employee-open-bulk-import-button">Bulk Import Employees</Button>
              <Dialog open={showEmployeeModal} onOpenChange={setShowEmployeeModal}>
              <DialogTrigger asChild>
                <Button type="button" className="rounded-2xl bg-[#F4A300] text-[#0D1B2A] hover:bg-[#D89000]" data-testid="employee-open-create-modal-button">Create New Employee</Button>
              </DialogTrigger>
              <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto rounded-[28px] border border-[#0D1B2A]/10 bg-white p-0">
                <DialogHeader className="border-b border-slate-200 px-6 py-5">
                  <DialogTitle data-testid="employee-create-modal-title">Create New Employee</DialogTitle>
                  <DialogDescription data-testid="employee-create-modal-description">Add profile details, organisation mapping, and attendance method assignment in one quick modal.</DialogDescription>
                </DialogHeader>
                <form className="grid gap-4 px-6 py-6 md:grid-cols-2 xl:grid-cols-3" onSubmit={handleCreateEmployee} data-testid="employee-create-form">
                  <Input placeholder="Full name" value={employeeForm.name} onChange={(event) => setEmployeeForm((current) => ({ ...current, name: event.target.value }))} data-testid="employee-name-input" />
                  <Input placeholder="Email" value={employeeForm.email} onChange={(event) => setEmployeeForm((current) => ({ ...current, email: event.target.value }))} data-testid="employee-email-input" />
                  <Input placeholder="Phone (optional)" value={employeeForm.phone} onChange={(event) => setEmployeeForm((current) => ({ ...current, phone: event.target.value }))} data-testid="employee-phone-input" />
                  <Input placeholder="Employee code" value={employeeForm.employee_code} onChange={(event) => setEmployeeForm((current) => ({ ...current, employee_code: event.target.value }))} data-testid="employee-code-input" />
                  <Input placeholder="Aadhar number" value={employeeForm.aadhar_number} onChange={(event) => setEmployeeForm((current) => ({ ...current, aadhar_number: event.target.value }))} data-testid="employee-aadhar-input" />
                  <select className="h-11 rounded-2xl border border-slate-200 px-3 text-sm" value={employeeForm.role} onChange={(event) => setEmployeeForm((current) => ({ ...current, role: event.target.value }))} data-testid="employee-role-select">
                    <option value="employee">Employee</option>
                    <option value="manager">Manager</option>
                  </select>
                  <select className="h-11 rounded-2xl border border-slate-200 px-3 text-sm" value={employeeForm.department_id} onChange={(event) => setEmployeeForm((current) => ({ ...current, department_id: event.target.value }))} data-testid="employee-department-select">
                    {referenceData.departments.map((department) => <option key={department.id} value={department.id}>{department.name}</option>)}
                  </select>
                  <select className="h-11 rounded-2xl border border-slate-200 px-3 text-sm" value={employeeForm.team_id} onChange={(event) => setEmployeeForm((current) => ({ ...current, team_id: event.target.value }))} data-testid="employee-team-select">
                    {referenceData.teams.map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
                  </select>
                  <select className="h-11 rounded-2xl border border-slate-200 px-3 text-sm" value={employeeForm.shift_id} onChange={(event) => setEmployeeForm((current) => ({ ...current, shift_id: event.target.value }))} data-testid="employee-shift-select">
                    {referenceData.shifts.map((shift) => <option key={shift.id} value={shift.id}>{shift.name}</option>)}
                  </select>
                  <select className="h-11 rounded-2xl border border-slate-200 px-3 text-sm" value={employeeForm.location_id} onChange={(event) => setEmployeeForm((current) => ({ ...current, location_id: event.target.value }))} data-testid="employee-location-select">
                    {referenceData.locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
                  </select>
                  <select className="h-11 rounded-2xl border border-slate-200 px-3 text-sm" value={employeeForm.business_unit_id} onChange={(event) => setEmployeeForm((current) => ({ ...current, business_unit_id: event.target.value }))} data-testid="employee-business-unit-select">
                    {referenceData.business_units.map((businessUnit) => <option key={businessUnit.id} value={businessUnit.id}>{businessUnit.name}</option>)}
                  </select>
                  <select className="h-11 rounded-2xl border border-slate-200 px-3 text-sm" value={employeeForm.manager_user_id} onChange={(event) => setEmployeeForm((current) => ({ ...current, manager_user_id: event.target.value }))} data-testid="employee-manager-select">
                    <option value="">No manager</option>
                    {managerOptions.map((manager) => <option key={manager.id} value={manager.user_id}>{manager.name}</option>)}
                  </select>
                  {requiresAttendanceAssignment ? (
                    <select className="h-11 rounded-2xl border border-slate-200 px-3 text-sm md:col-span-2 xl:col-span-1" value={employeeForm.assigned_attendance_feature} onChange={(event) => setEmployeeForm((current) => ({ ...current, assigned_attendance_feature: event.target.value }))} data-testid="employee-assignment-select">
                      <option value="">Select attendance method</option>
                      {attendanceOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                  ) : (
                    <div className="rounded-3xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500 md:col-span-2 xl:col-span-1" data-testid="employee-assignment-default-card">
                      Auto-assigned attendance method: <span className="font-semibold text-slate-900">{getAttendanceFeatureLabel(defaultAttendanceAssignment)}</span>
                    </div>
                  )}
                  <div className="rounded-3xl border border-slate-200 bg-slate-50 px-4 py-3 md:col-span-2 xl:col-span-2" data-testid="employee-profile-picture-field">
                    <p className="text-sm font-semibold text-slate-900">Profile picture</p>
                    <p className="mt-1 text-xs text-slate-500">Optional, but strongly recommended for photo scanner attendance verification.</p>
                    <Input type="file" accept="image/*" className="mt-3" onChange={handleEmployeeProfileUpload} data-testid="employee-profile-picture-input" />
                    {employeeForm.profile_picture_data ? <p className="mt-2 text-xs font-semibold text-emerald-700" data-testid="employee-profile-picture-ready">Profile picture attached</p> : null}
                  </div>
                  {leaveSetup.leave_enabled ? (
                    <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 md:col-span-2 xl:col-span-3" data-testid="employee-leave-assignment-card">
                      <p className="font-semibold text-slate-900">Leave assignment</p>
                      <p className="mt-1 text-sm text-slate-500">Choose one or more company leave types and set the opening balance for each.</p>
                      <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                        {leaveTypeOptions.map((leaveType) => {
                          const existing = employeeForm.leave_assignments.find((item) => item.leave_type_code === leaveType.code);
                          return (
                            <div key={leaveType.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`employee-leave-option-${leaveType.code}`}>
                              <label className="flex items-center gap-3 text-sm font-semibold text-slate-900">
                                <input type="checkbox" checked={Boolean(existing)} onChange={(event) => toggleLeaveAssignment(leaveType.code, event.target.checked, employeeForm.leave_assignments, setEmployeeForm)} data-testid={`employee-leave-checkbox-${leaveType.code}`} />
                                <span>{leaveType.name}</span>
                              </label>
                              {existing ? <Input type="number" min="0" className="mt-3" value={existing.opening_balance} onChange={(event) => updateLeaveAssignmentBalance(leaveType.code, event.target.value, employeeForm.leave_assignments, setEmployeeForm)} data-testid={`employee-leave-balance-input-${leaveType.code}`} /> : null}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                  <div className="flex flex-wrap justify-end gap-3 md:col-span-2 xl:col-span-3">
                    <Button type="button" variant="outline" className="rounded-2xl" onClick={() => setShowEmployeeModal(false)} data-testid="employee-create-cancel-button">Cancel</Button>
                    <Button type="submit" className="rounded-2xl bg-[#F4A300] text-[#0D1B2A] hover:bg-[#D89000]" data-testid="employee-create-submit-button">Create employee</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            </div>
          </div>
        </CardHeader>
      </Card>

      {renderBulkModal()}

      <Card className="border-slate-200/70 bg-white/85" data-testid="employee-directory-card">
        <CardHeader>
          <CardTitle data-testid="employee-directory-title">Employee directory</CardTitle>
          <CardDescription data-testid="employee-directory-description">Browse employees in a cleaner card view, then click any card to open the full employee profile.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-6" data-testid="employee-directory-filters">
            <Input value={employeeSearch} onChange={(event) => setEmployeeSearch(event.target.value)} placeholder="Search by name, code, or email" data-testid="employee-directory-search-input" />
            <select className="h-11 rounded-2xl border border-slate-200 px-3 text-sm" value={employeeFilters.lifecycle} onChange={(event) => setEmployeeFilters((current) => ({ ...current, lifecycle: event.target.value }))} data-testid="employee-directory-lifecycle-filter">
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="all">All</option>
            </select>
            <select className="h-11 rounded-2xl border border-slate-200 px-3 text-sm" value={employeeFilters.department} onChange={(event) => setEmployeeFilters((current) => ({ ...current, department: event.target.value }))} data-testid="employee-directory-department-filter">
              <option value="">All departments</option>
              {[...new Set(referenceData.employees.map((employee) => employee.today_snapshot?.department_name).filter(Boolean))].map((departmentName) => <option key={departmentName} value={departmentName}>{departmentName}</option>)}
            </select>
            <select className="h-11 rounded-2xl border border-slate-200 px-3 text-sm" value={employeeFilters.location} onChange={(event) => setEmployeeFilters((current) => ({ ...current, location: event.target.value }))} data-testid="employee-directory-location-filter">
              <option value="">All locations</option>
              {referenceData.locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
            </select>
            <select className="h-11 rounded-2xl border border-slate-200 px-3 text-sm" value={employeeFilters.attendance} onChange={(event) => setEmployeeFilters((current) => ({ ...current, attendance: event.target.value }))} data-testid="employee-directory-attendance-filter">
              <option value="">All attendance methods</option>
              {attendanceOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
            <select className="h-11 rounded-2xl border border-slate-200 px-3 text-sm" value={employeeFilters.status} onChange={(event) => setEmployeeFilters((current) => ({ ...current, status: event.target.value }))} data-testid="employee-directory-status-filter">
              <option value="">All statuses</option>
              {['In', 'Out', 'Absent', 'On Leave'].map((statusValue) => <option key={statusValue} value={statusValue}>{statusValue}</option>)}
            </select>
          </div>

          <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3" data-testid="employee-card-grid">
            {filteredEmployees.map((employee) => (
              <div key={employee.id} role="button" tabIndex={0} className="rounded-[28px] border border-slate-200 bg-white p-5 text-left shadow-[0_14px_38px_rgba(13,27,42,0.05)] transition hover:-translate-y-0.5 hover:border-[#F4A300]/30" onClick={() => navigate(`/company/employees/${employee.id}`)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") navigate(`/company/employees/${employee.id}`); }} data-testid={`employee-card-${employee.id}`}>
                <div className="flex items-start gap-4">
                  {employee.profile_picture_data ? <img src={employee.profile_picture_data} alt={employee.name} className="h-16 w-16 rounded-2xl object-cover" data-testid={`employee-card-image-${employee.id}`} /> : <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#0D1B2A] text-xl font-bold text-white" data-testid={`employee-card-initials-${employee.id}`}>{employee.name?.slice(0, 1)}</div>}
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="text-lg font-bold text-slate-900" data-testid={`employee-card-name-${employee.id}`}>{employee.name}</p>
                        <p className="text-xs text-slate-500" data-testid={`employee-card-code-${employee.id}`}>{employee.employee_code} • {employee.email}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${(employee.status || (employee.active === false ? "inactive" : "active")) === "inactive" ? "bg-slate-200 text-slate-600" : "bg-emerald-100 text-emerald-700"}`} data-testid={`employee-card-lifecycle-${employee.id}`}>{(employee.status || (employee.active === false ? "inactive" : "active")) === "inactive" ? "Inactive" : "Active"}</span>
                        <StatusBadge value={employee.today_snapshot?.current_status || "Absent"} testId={`employee-card-status-${employee.id}`} />
                      </div>
                    </div>
                    <div className="grid gap-2 text-sm text-slate-600 md:grid-cols-2">
                      <p data-testid={`employee-card-department-${employee.id}`}><span className="font-semibold text-slate-900">Department / Team:</span> {employee.today_snapshot?.department_name || "—"} / {employee.today_snapshot?.team_name || "—"}</p>
                      <p data-testid={`employee-card-location-${employee.id}`}><span className="font-semibold text-slate-900">Location:</span> {employee.location_name || "—"}</p>
                      <p data-testid={`employee-card-business-unit-${employee.id}`}><span className="font-semibold text-slate-900">Business Unit:</span> {employee.business_unit_name || "—"}</p>
                      <p data-testid={`employee-card-attendance-${employee.id}`}><span className="font-semibold text-slate-900">Attendance:</span> {employee.assigned_attendance_label}</p>
                    </div>
                    <div className="flex flex-wrap gap-2 pt-2">
                      <select
                        className="h-10 rounded-2xl border border-slate-200 px-3 text-sm"
                        value={assignmentDrafts[employee.id] || defaultAttendanceAssignment}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event) => setAssignmentDrafts((current) => ({ ...current, [employee.id]: event.target.value }))}
                        data-testid={`employee-card-assignment-select-${employee.id}`}
                      >
                        {!requiresAttendanceAssignment ? <option value={defaultAttendanceAssignment}>{getAttendanceFeatureLabel(defaultAttendanceAssignment)}</option> : <option value="">Select method</option>}
                        {employee.available_attendance_features.map((feature) => <option key={feature} value={feature}>{getAttendanceFeatureLabel(feature)}</option>)}
                      </select>
                      <Button type="button" variant="outline" className="rounded-2xl" onClick={(event) => { event.stopPropagation(); handleSaveEmployeeAssignment(employee); }} disabled={savingEmployeeId === employee.id || (assignmentDrafts[employee.id] || defaultAttendanceAssignment) === employee.assigned_attendance_feature} data-testid={`employee-card-save-assignment-${employee.id}`}>{savingEmployeeId === employee.id ? "Saving..." : "Save"}</Button>
                      {employee.leave_enabled ? <Button type="button" variant="outline" className="rounded-2xl" onClick={(event) => { event.stopPropagation(); handleSelectEmployeeLeave(employee); }} data-testid={`employee-card-manage-leave-${employee.id}`}>Leave</Button> : null}
                      <Button type="button" variant="outline" className="rounded-2xl" onClick={(event) => { event.stopPropagation(); handleResetEmployeePassword(employee); }} disabled={resettingEmployeeId === employee.id} data-testid={`employee-card-reset-password-${employee.id}`}>{resettingEmployeeId === employee.id ? "Resetting..." : "Reset password"}</Button>
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-3 pt-2 text-xs text-slate-500">
                      <span data-testid={`employee-card-aadhar-${employee.id}`}>Aadhar: {employee.masked_aadhar_number || "—"}</span>
                      <span className={`inline-flex rounded-full px-3 py-1 font-semibold ${employee.must_reset_password ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`} data-testid={`employee-card-password-state-${employee.id}`}>{employee.must_reset_password ? "Reset pending" : "Active password"}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200/70 bg-white/85" data-testid="scanner-admin-card">
        <CardHeader>
          <CardTitle data-testid="scanner-admin-title">Scanner Admins</CardTitle>
          <CardDescription data-testid="scanner-admin-description">Scanner Admins are restricted to exactly one location and only operate the photo scanner attendance station.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <form className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" onSubmit={handleCreateScannerAdmin} data-testid="scanner-admin-create-form">
            <Input placeholder="Full name" value={scannerAdminForm.name} onChange={(event) => setScannerAdminForm((current) => ({ ...current, name: event.target.value }))} data-testid="scanner-admin-name-input" />
            <Input placeholder="Email" value={scannerAdminForm.email} onChange={(event) => setScannerAdminForm((current) => ({ ...current, email: event.target.value }))} data-testid="scanner-admin-email-input" />
            <Input placeholder="Phone (optional)" value={scannerAdminForm.phone} onChange={(event) => setScannerAdminForm((current) => ({ ...current, phone: event.target.value }))} data-testid="scanner-admin-phone-input" />
            <select className="h-11 rounded-2xl border border-slate-200 px-3 text-sm" value={scannerAdminForm.location_id} onChange={(event) => setScannerAdminForm((current) => ({ ...current, location_id: event.target.value }))} data-testid="scanner-admin-location-select">
              {referenceData.locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
            </select>
            <Button type="submit" className="rounded-2xl bg-[#0D1B2A] text-white hover:bg-[#08131F] xl:col-span-4" data-testid="scanner-admin-create-submit-button">Create Scanner Admin</Button>
          </form>

          <div className="overflow-hidden rounded-3xl border border-slate-200/70" data-testid="scanner-admin-table-wrap">
            <Table data-testid="scanner-admin-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {referenceData.scanner_admins.map((scannerAdmin) => (
                  <TableRow key={scannerAdmin.id} data-testid={`scanner-admin-row-${scannerAdmin.id}`}>
                    <TableCell>{scannerAdmin.name}</TableCell>
                    <TableCell>{scannerAdmin.email}</TableCell>
                    <TableCell>
                      <select className="h-10 w-full rounded-2xl border border-slate-200 px-3 text-sm" value={scannerAdmin.location_id || ""} onChange={(event) => handleUpdateScannerAdmin(scannerAdmin, { location_id: event.target.value })} data-testid={`scanner-admin-location-edit-${scannerAdmin.id}`}>
                        {referenceData.locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
                      </select>
                    </TableCell>
                    <TableCell>{scannerAdmin.active ? "Active" : "Inactive"}</TableCell>
                    <TableCell>
                      <Button type="button" variant="outline" className="rounded-2xl" onClick={() => handleUpdateScannerAdmin(scannerAdmin, { active: !scannerAdmin.active })} data-testid={`scanner-admin-active-toggle-${scannerAdmin.id}`}>{scannerAdmin.active ? "Deactivate" : "Activate"}</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={Boolean(passwordResetResult)} onOpenChange={(open) => { if (!open) setPasswordResetResult(null); }}>
        <DialogContent className="max-w-lg rounded-[28px] border border-[#0D1B2A]/10 bg-white">
          <DialogHeader>
            <DialogTitle data-testid="employee-reset-password-modal-title">Temporary password ready</DialogTitle>
            <DialogDescription data-testid="employee-reset-password-modal-description">Share this password with the employee now. It will only be shown once on this screen.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4" data-testid="employee-reset-password-modal">
            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#C27D00]">Employee</p>
              <p className="mt-2 font-semibold text-slate-900" data-testid="employee-reset-password-modal-employee-name">{passwordResetResult?.employeeName}</p>
              <div className="mt-4 rounded-2xl border border-dashed border-[#F4A300]/35 bg-white px-4 py-3 text-lg font-bold tracking-[0.08em] text-[#0D1B2A]" data-testid="employee-reset-password-modal-value">{passwordResetResult?.temporaryPassword}</div>
            </div>
            <div className="flex flex-wrap justify-end gap-3">
              <Button type="button" variant="outline" className="rounded-2xl" onClick={() => navigator.clipboard.writeText(passwordResetResult?.temporaryPassword || "")} data-testid="employee-reset-password-copy-button">Copy Password</Button>
              <Button type="button" className="rounded-2xl bg-[#F4A300] text-[#0D1B2A] hover:bg-[#D89000]" onClick={() => setPasswordResetResult(null)} data-testid="employee-reset-password-close-button">Done</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {selectedEmployeeForLeave ? (
        <Card className="border-slate-200/70 bg-white/85" data-testid="employee-leave-editor-card">
          <CardHeader>
            <CardTitle data-testid="employee-leave-editor-title">Edit leave assignment for {selectedEmployeeForLeave.name}</CardTitle>
            <CardDescription data-testid="employee-leave-editor-description">Update the leave types and opening balance for this employee.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {leaveTypeOptions.map((leaveType) => {
                const existing = selectedEmployeeForLeave.leave_assignments.find((item) => item.leave_type_code === leaveType.code);
                return (
                  <div key={leaveType.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid={`employee-edit-leave-option-${leaveType.code}`}>
                    <label className="flex items-center gap-3 text-sm font-semibold text-slate-900">
                      <input type="checkbox" checked={Boolean(existing)} onChange={(event) => toggleLeaveAssignment(leaveType.code, event.target.checked, selectedEmployeeForLeave.leave_assignments, setSelectedEmployeeForLeave)} data-testid={`employee-edit-leave-checkbox-${leaveType.code}`} />
                      <span>{leaveType.name}</span>
                    </label>
                    {existing ? <Input type="number" min="0" className="mt-3" value={existing.opening_balance} onChange={(event) => updateLeaveAssignmentBalance(leaveType.code, event.target.value, selectedEmployeeForLeave.leave_assignments, setSelectedEmployeeForLeave)} data-testid={`employee-edit-leave-balance-${leaveType.code}`} /> : null}
                  </div>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-3">
              <Button type="button" className="rounded-2xl bg-[#F4A300] text-[#0D1B2A] hover:bg-[#D89000]" onClick={handleSaveEmployeeLeaveAssignments} data-testid="employee-leave-editor-save-button">Save leave assignment</Button>
              <Button type="button" variant="outline" className="rounded-2xl" onClick={() => setSelectedEmployeeForLeave(null)} data-testid="employee-leave-editor-cancel-button">Cancel</Button>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </>
  );

  const renderAttendance = () => (
    <>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5" data-testid="attendance-summary-grid">
        <StatCard label="Present" value={monitorData?.summary?.present || 0} accent="#F4A300" helper="Checked in or closed out" testId="attendance-stat-present" />
        <StatCard label="Absent" value={monitorData?.summary?.absent || 0} accent="#EF4444" helper="Missing attendance today" testId="attendance-stat-absent" />
        <StatCard label="Late" value={monitorData?.summary?.late || 0} accent="#F59E0B" helper="Late after grace period" testId="attendance-stat-late" />
        <StatCard label="Leave" value={monitorData?.summary?.leave || 0} accent="#0D1B2A" helper="Approved leave today" testId="attendance-stat-leave" />
        <StatCard label="Overtime" value={formatMinutes(monitorData?.summary?.overtime_minutes || 0)} accent="#0D1B2A" helper="Total overtime minutes" testId="attendance-stat-overtime" />
      </div>

      <Card className="border-slate-200/70 bg-white/85" data-testid="attendance-monitor-card">
        <CardHeader>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <CardTitle data-testid="attendance-monitor-title">Daily attendance table</CardTitle>
              <CardDescription data-testid="attendance-monitor-description">Use the inline correction controls for missed or incorrect punches.</CardDescription>
            </div>
            <Input type="date" value={selectedDate} onChange={(event) => setSelectedDate(event.target.value)} className="max-w-[220px] rounded-2xl border-slate-200" data-testid="attendance-date-input" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-hidden rounded-3xl border border-slate-200/70" data-testid="attendance-monitor-table-wrap">
            <Table data-testid="attendance-monitor-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Clock In</TableHead>
                  <TableHead>Clock Out</TableHead>
                  <TableHead>Hours</TableHead>
                  <TableHead>Correction</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(monitorData?.rows || []).map((row) => {
                  const record = row.record;
                  const draft = correctionDrafts[record?.id] || { clock_in_time: timeValue(record?.clock_in), clock_out_time: timeValue(record?.clock_out), notes: record?.notes || "" };
                  return (
                    <TableRow key={row.employee_id} data-testid={`attendance-monitor-row-${row.employee_id}`}>
                      <TableCell>
                        <div>
                          <p className="font-semibold text-slate-900">{row.employee_name}</p>
                          <p className="text-xs text-slate-500">{row.employee_code}</p>
                        </div>
                      </TableCell>
                      <TableCell><StatusBadge value={row.current_status} testId={`attendance-monitor-status-${row.employee_id}`} /></TableCell>
                      <TableCell>{extractTime(record?.clock_in)}</TableCell>
                      <TableCell>{extractTime(record?.clock_out)}</TableCell>
                      <TableCell>{formatHours(row.total_hours)}</TableCell>
                      <TableCell>
                        {record ? (
                          <div className="grid gap-2 lg:grid-cols-[110px_110px_1fr_auto]" data-testid={`attendance-correction-controls-${record.id}`}>
                            <Input type="time" value={draft.clock_in_time} onChange={(event) => setCorrectionDrafts((current) => ({ ...current, [record.id]: { ...draft, clock_in_time: event.target.value } }))} data-testid={`attendance-correction-clockin-${record.id}`} />
                            <Input type="time" value={draft.clock_out_time} onChange={(event) => setCorrectionDrafts((current) => ({ ...current, [record.id]: { ...draft, clock_out_time: event.target.value } }))} data-testid={`attendance-correction-clockout-${record.id}`} />
                            <Input value={draft.notes} onChange={(event) => setCorrectionDrafts((current) => ({ ...current, [record.id]: { ...draft, notes: event.target.value } }))} placeholder="Correction note" data-testid={`attendance-correction-note-${record.id}`} />
                            <Button type="button" className="rounded-2xl bg-[#0D1B2A] text-white hover:bg-[#08131F]" onClick={() => handleCorrection(record.id)} data-testid={`attendance-correction-save-${record.id}`}>
                              Save
                            </Button>
                          </div>
                        ) : (
                          <span className="text-sm text-slate-400" data-testid={`attendance-no-record-${row.employee_id}`}>No punch to correct</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </>
  );

  const renderApprovals = () => (
    <div className="grid gap-6 xl:grid-cols-2">
      <Card className="border-slate-200/70 bg-white/85" data-testid="leave-approvals-card">
        <CardHeader>
          <CardTitle data-testid="leave-approvals-title">Pending leave requests</CardTitle>
          <CardDescription data-testid="leave-approvals-description">Approve leave only within your tenant scope.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {approvals.leave_requests.map((request) => (
            <div key={request.id} className="rounded-3xl border border-slate-200 bg-slate-50/80 p-4" data-testid={`leave-approval-item-${request.id}`}>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-semibold text-slate-900">{employeeMap[request.employee_id]?.name || request.employee_id}</p>
                  <p className="text-sm text-slate-500">{request.leave_type_name} • {formatDate(request.start_date)} to {formatDate(request.end_date)}</p>
                </div>
                <StatusBadge value={request.status} testId={`leave-approval-status-${request.id}`} />
              </div>
              <p className="mt-3 text-sm text-slate-500">{request.reason}</p>
              <div className="mt-4 flex gap-2">
                <Button type="button" className="rounded-2xl bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => handleLeaveDecision(request.id, "approved")} data-testid={`leave-approval-approve-${request.id}`}>Approve</Button>
                <Button type="button" variant="outline" className="rounded-2xl" onClick={() => handleLeaveDecision(request.id, "rejected")} data-testid={`leave-approval-reject-${request.id}`}>Reject</Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="border-slate-200/70 bg-white/85" data-testid="regularization-approvals-card">
        <CardHeader>
          <CardTitle data-testid="regularization-approvals-title">Pending regularizations</CardTitle>
          <CardDescription data-testid="regularization-approvals-description">Approve attendance corrections after checking requested punch times.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {approvals.regularization_requests.map((request) => (
            <div key={request.id} className="rounded-3xl border border-slate-200 bg-slate-50/80 p-4" data-testid={`regularization-item-${request.id}`}>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-semibold text-slate-900">{employeeMap[request.employee_id]?.name || request.employee_id}</p>
                  <p className="text-sm text-slate-500">{formatDate(request.date)} • {extractTime(request.requested_clock_in)} → {extractTime(request.requested_clock_out)}</p>
                </div>
                <StatusBadge value={request.status} testId={`regularization-status-${request.id}`} />
              </div>
              <p className="mt-3 text-sm text-slate-500">{request.reason}</p>
              <div className="mt-4 flex gap-2">
                <Button type="button" className="rounded-2xl bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => handleRegularizationDecision(request.id, "approved")} data-testid={`regularization-approve-${request.id}`}>Approve</Button>
                <Button type="button" variant="outline" className="rounded-2xl" onClick={() => handleRegularizationDecision(request.id, "rejected")} data-testid={`regularization-reject-${request.id}`}>Reject</Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );

  const renderKiosk = () => {
    if (!featureFlags?.kiosk_qr_attendance) {
      return <Card className="border-slate-200/70 bg-white/85 p-10 text-center text-slate-500" data-testid="kiosk-disabled-card">Kiosk QR attendance is disabled for this tenant.</Card>;
    }

    return (
      <>
        <Card className="border-slate-200/70 bg-white/85" data-testid="kiosk-create-card">
          <CardHeader>
            <CardTitle data-testid="kiosk-create-title">Create kiosk session</CardTitle>
            <CardDescription data-testid="kiosk-create-description">Each session stays tenant-locked, and its live QR rotates automatically every minute.</CardDescription>
          </CardHeader>
          <CardContent>
            <form className="grid gap-4 md:grid-cols-[1fr_180px_auto]" onSubmit={handleCreateSession} data-testid="kiosk-create-form">
              <Input value={sessionForm.label} onChange={(event) => setSessionForm((current) => ({ ...current, label: event.target.value }))} placeholder="Session label" data-testid="kiosk-label-input" />
              <Input type="number" value={sessionForm.expires_minutes} onChange={(event) => setSessionForm((current) => ({ ...current, expires_minutes: Number(event.target.value) }))} data-testid="kiosk-expiry-input" />
              <Button type="submit" className="rounded-2xl bg-[#F4A300] text-[#0D1B2A] hover:bg-[#D89000]" data-testid="kiosk-create-submit-button">Generate QR</Button>
            </form>
          </CardContent>
        </Card>

        {sessions.length ? (
          <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3" data-testid="kiosk-session-grid">
            {sessions.map((session) => (
              <KioskSessionCard
                key={session.id}
                session={session}
                isOpening={openingSessionId === session.id}
                onOpen={handleOpenKioskWindow}
              />
            ))}
          </div>
        ) : (
          <Card className="border-slate-200/70 bg-white/85 p-10 text-center text-slate-500" data-testid="kiosk-empty-state-card">
            No kiosk sessions yet. Create one to open a rotating live QR window.
          </Card>
        )}
      </>
    );
  };

  const renderLeaveSettings = () => (
    <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
      <Card className="border-slate-200/70 bg-white/85" data-testid="leave-settings-config-card">
        <CardHeader>
          <CardTitle data-testid="leave-settings-config-title">Company leave configuration</CardTitle>
          <CardDescription data-testid="leave-settings-config-description">Leave stays disabled until you explicitly turn it on and define your company policy.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3" data-testid="leave-settings-enabled-row">
            <button type="button" className={`rounded-full border px-4 py-2 text-sm font-semibold ${leaveSettingsForm.enabled ? "border-[#F4A300]/25 bg-[#F4A300]/10 text-[#C27D00]" : "border-slate-200 bg-white text-slate-500"}`} onClick={() => setLeaveSettingsForm((current) => ({ ...current, enabled: !current.enabled }))} data-testid="leave-settings-enabled-toggle">{leaveSettingsForm.enabled ? "Leave enabled" : "Leave disabled"}</button>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <select className="h-11 rounded-2xl border border-slate-200 px-3 text-sm" value={leaveSettingsForm.leave_year_type} onChange={(event) => setLeaveSettingsForm((current) => ({ ...current, leave_year_type: event.target.value }))} data-testid="leave-settings-year-select">
              <option value="calendar_year">Calendar Year</option>
              <option value="financial_year">Financial Year</option>
            </select>
            <select className="h-11 rounded-2xl border border-slate-200 px-3 text-sm" value={leaveSettingsForm.credit_method} onChange={(event) => setLeaveSettingsForm((current) => ({ ...current, credit_method: event.target.value }))} data-testid="leave-settings-credit-method-select">
              <option value="manual">Manual</option>
              <option value="automatic">Automatic</option>
            </select>
            <button type="button" className={`rounded-full border px-4 py-2 text-sm font-semibold ${leaveSettingsForm.carry_forward_enabled ? "border-[#F4A300]/25 bg-[#F4A300]/10 text-[#C27D00]" : "border-slate-200 bg-white text-slate-500"}`} onClick={() => setLeaveSettingsForm((current) => ({ ...current, carry_forward_enabled: !current.carry_forward_enabled }))} data-testid="leave-settings-carry-toggle">Carry forward {leaveSettingsForm.carry_forward_enabled ? "enabled" : "disabled"}</button>
            <Input type="number" min="0" value={leaveSettingsForm.carry_forward_limit} onChange={(event) => setLeaveSettingsForm((current) => ({ ...current, carry_forward_limit: Number(event.target.value) }))} placeholder="Carry forward limit" data-testid="leave-settings-carry-limit-input" disabled={!leaveSettingsForm.carry_forward_enabled} />
          </div>
          <Button type="button" className="rounded-2xl bg-[#F4A300] text-[#0D1B2A] hover:bg-[#D89000]" onClick={handleSaveLeaveSettings} data-testid="leave-settings-save-button">Save leave settings</Button>
        </CardContent>
      </Card>

      <Card className="border-slate-200/70 bg-white/85" data-testid="leave-types-card">
        <CardHeader>
          <CardTitle data-testid="leave-types-title">Leave types</CardTitle>
          <CardDescription data-testid="leave-types-description">Create only the leave types your company wants employees to use.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[120px_1fr_160px_auto]">
            <Input value={leaveTypeForm.code} onChange={(event) => setLeaveTypeForm((current) => ({ ...current, code: event.target.value.toUpperCase() }))} placeholder="Code" data-testid="leave-type-code-input" />
            <Input value={leaveTypeForm.name} onChange={(event) => setLeaveTypeForm((current) => ({ ...current, name: event.target.value }))} placeholder="Leave type name" data-testid="leave-type-name-input" />
            <Input type="number" min="0" value={leaveTypeForm.monthly_credit} onChange={(event) => setLeaveTypeForm((current) => ({ ...current, monthly_credit: Number(event.target.value) }))} placeholder="Monthly credit" data-testid="leave-type-monthly-credit-input" disabled={leaveSettingsForm.credit_method !== "automatic"} />
            <Button type="button" className="rounded-2xl bg-[#0D1B2A] text-white hover:bg-[#08131F]" onClick={handleCreateLeaveType} data-testid="leave-type-create-button">Add</Button>
          </div>
          <div className="grid gap-3" data-testid="leave-type-list">
            {leaveSetup.leave_types.map((leaveType) => (
              <div key={leaveType.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid={`leave-type-item-${leaveType.id}`}>
                <div>
                  <p className="font-semibold text-slate-900">{leaveType.name} <span className="text-xs text-slate-400">({leaveType.code})</span></p>
                  <p className="text-sm text-slate-500">Monthly credit: {leaveType.monthly_credit || 0}</p>
                </div>
                <Button type="button" variant="outline" className="rounded-2xl" onClick={() => handleToggleLeaveType(leaveType)} data-testid={`leave-type-toggle-${leaveType.id}`}>{leaveType.active ? "Disable" : "Enable"}</Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderReports = () => (
    <>
      <Card className="border-slate-200/70 bg-white/85" data-testid="reports-filter-card">
        <CardHeader>
          <CardTitle data-testid="reports-filter-title">Report filters</CardTitle>
          <CardDescription data-testid="reports-filter-description">Run tenant-safe Excel exports by period, department, and employee.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 lg:grid-cols-[160px_180px_1fr_1fr_auto_auto]">
          <select className="h-11 rounded-2xl border border-slate-200 px-3 text-sm" value={reportFilters.period} onChange={(event) => setReportFilters((current) => ({ ...current, period: event.target.value }))} data-testid="reports-period-select">
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
          <Input type="date" value={reportFilters.anchor_date} onChange={(event) => setReportFilters((current) => ({ ...current, anchor_date: event.target.value }))} data-testid="reports-anchor-date-input" />
          <select className="h-11 rounded-2xl border border-slate-200 px-3 text-sm" value={reportFilters.department_id} onChange={(event) => setReportFilters((current) => ({ ...current, department_id: event.target.value }))} data-testid="reports-department-select">
            <option value="">All departments</option>
            {referenceData.departments.map((department) => <option key={department.id} value={department.id}>{department.name}</option>)}
          </select>
          <select className="h-11 rounded-2xl border border-slate-200 px-3 text-sm" value={reportFilters.employee_id} onChange={(event) => setReportFilters((current) => ({ ...current, employee_id: event.target.value }))} data-testid="reports-employee-select">
            <option value="">All employees</option>
            {referenceData.employees.map((employee) => <option key={employee.id} value={employee.id}>{employee.name}</option>)}
          </select>
          <Button type="button" variant="outline" className="rounded-2xl" onClick={loadReports} data-testid="reports-refresh-button">Refresh</Button>
          <Button type="button" className="rounded-2xl bg-[#F4A300] text-[#0D1B2A] hover:bg-[#D89000]" onClick={exportReports} data-testid="reports-export-button">Export Excel</Button>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="reports-summary-grid">
        <StatCard label="Rows" value={reportData?.summary?.total_rows || 0} accent="#0D1B2A" helper="Entries in current report" testId="reports-stat-rows" />
        <StatCard label="Late" value={reportData?.summary?.late_entries || 0} accent="#F59E0B" helper="Late entries in range" testId="reports-stat-late" />
        <StatCard label="Absent" value={reportData?.summary?.absent_entries || 0} accent="#EF4444" helper="Absence rows in range" testId="reports-stat-absent" />
        <StatCard label="Hours" value={reportData?.summary?.hours_worked || 0} accent="#F4A300" helper="Total hours worked" testId="reports-stat-hours" />
      </div>

      <Card className="border-slate-200/70 bg-white/85" data-testid="reports-table-card">
        <CardHeader>
          <CardTitle data-testid="reports-table-title">Report preview</CardTitle>
          <CardDescription data-testid="reports-table-description">This preview mirrors the Excel export structure.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-hidden rounded-3xl border border-slate-200/70" data-testid="reports-table-wrap">
            <Table data-testid="reports-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Employee</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Hours</TableHead>
                  <TableHead>Late</TableHead>
                  <TableHead>OT</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(reportData?.rows || []).slice(0, 60).map((row, index) => (
                  <TableRow key={`${row.employee_code}-${row.date}-${index}`} data-testid={`reports-row-${index}`}>
                    <TableCell>{formatDate(row.date)}</TableCell>
                    <TableCell>{row.employee_name}</TableCell>
                    <TableCell><StatusBadge value={row.status} testId={`reports-status-${index}`} /></TableCell>
                    <TableCell>{formatHours(row.total_hours)}</TableCell>
                    <TableCell>{formatMinutes(row.late_minutes)}</TableCell>
                    <TableCell>{formatMinutes(row.overtime_minutes)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </>
  );

  const renderSettings = () => (
    <div className="grid gap-6">
      <Card className="border-slate-200/70 bg-white/85" data-testid="settings-policy-card">
        <CardHeader>
          <CardTitle data-testid="settings-policy-title">Attendance policy</CardTitle>
          <CardDescription data-testid="settings-policy-description">Policy settings affect attendance validation at the business logic layer.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3" data-testid="settings-policy-toggles">
            <button type="button" className={`rounded-full border px-4 py-2 text-sm font-semibold ${policyForm.block_attendance_on_approved_leave ? "border-[#F4A300]/25 bg-[#F4A300]/10 text-[#C27D00]" : "border-slate-200 bg-white text-slate-500"}`} onClick={() => setPolicyForm((current) => ({ ...current, block_attendance_on_approved_leave: !current.block_attendance_on_approved_leave }))} data-testid="settings-policy-block-leave-toggle">Block attendance on approved leave</button>
            <button type="button" className={`rounded-full border px-4 py-2 text-sm font-semibold ${policyForm.allow_early_clock_in ? "border-[#F4A300]/25 bg-[#F4A300]/10 text-[#C27D00]" : "border-slate-200 bg-white text-slate-500"}`} onClick={() => setPolicyForm((current) => ({ ...current, allow_early_clock_in: !current.allow_early_clock_in }))} data-testid="settings-policy-early-clockin-toggle">Allow early clock-in</button>
            <button type="button" className={`rounded-full border px-4 py-2 text-sm font-semibold ${policyForm.allow_attendance_fallback ? "border-[#F4A300]/25 bg-[#F4A300]/10 text-[#C27D00]" : "border-slate-200 bg-white text-slate-500"}`} onClick={() => setPolicyForm((current) => ({ ...current, allow_attendance_fallback: !current.allow_attendance_fallback }))} data-testid="settings-policy-fallback-toggle">Allow fallback attendance methods</button>
            <button type="button" className={`rounded-full border px-4 py-2 text-sm font-semibold ${policyForm.allow_photo_scanner_without_profile_picture ? "border-[#F4A300]/25 bg-[#F4A300]/10 text-[#C27D00]" : "border-slate-200 bg-white text-slate-500"}`} onClick={() => setPolicyForm((current) => ({ ...current, allow_photo_scanner_without_profile_picture: !current.allow_photo_scanner_without_profile_picture }))} data-testid="settings-policy-photo-override-toggle">Allow photo scan override without profile picture</button>
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="settings-policy-threshold-grid">
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Local auto-approve %</p>
              <Input type="number" min="10" max="99" step="1" value={Math.round((policyForm.photo_scanner_local_approve_threshold || 0) * 100)} onChange={(event) => setPolicyForm((current) => ({ ...current, photo_scanner_local_approve_threshold: Number(event.target.value || 0) / 100 }))} data-testid="settings-policy-local-approve-threshold-input" />
            </div>
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Gemini fallback trigger %</p>
              <Input type="number" min="5" max="98" step="1" value={Math.round((policyForm.photo_scanner_local_fallback_threshold || 0) * 100)} onChange={(event) => setPolicyForm((current) => ({ ...current, photo_scanner_local_fallback_threshold: Number(event.target.value || 0) / 100 }))} data-testid="settings-policy-local-fallback-threshold-input" />
            </div>
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Gemini approve %</p>
              <Input type="number" min="10" max="99" step="1" value={Math.round((policyForm.photo_scanner_gemini_approve_threshold || 0) * 100)} onChange={(event) => setPolicyForm((current) => ({ ...current, photo_scanner_gemini_approve_threshold: Number(event.target.value || 0) / 100 }))} data-testid="settings-policy-gemini-approve-threshold-input" />
            </div>
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Gemini soft limit / day</p>
              <Input type="number" min="0" step="1" value={policyForm.photo_scanner_gemini_daily_soft_limit || 0} onChange={(event) => setPolicyForm((current) => ({ ...current, photo_scanner_gemini_daily_soft_limit: Number(event.target.value || 0) }))} data-testid="settings-policy-gemini-soft-limit-input" />
            </div>
          </div>
          <div className="space-y-2" data-testid="settings-weekly-off-block">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Weekly off days</p>
            <p className="text-xs text-slate-500">Used across all reports (LOP, Muster Roll, Monthly) to identify non-working days.</p>
            <div className="flex flex-wrap gap-2" data-testid="settings-weekly-off-days">
              {[["Mon", 0], ["Tue", 1], ["Wed", 2], ["Thu", 3], ["Fri", 4], ["Sat", 5], ["Sun", 6]].map(([label, dayIndex]) => {
                const active = (policyForm.weekly_off_days || []).includes(dayIndex);
                return (
                  <button
                    key={label}
                    type="button"
                    className={`rounded-full border px-4 py-2 text-sm font-semibold ${active ? "border-[#F4A300]/25 bg-[#F4A300]/10 text-[#C27D00]" : "border-slate-200 bg-white text-slate-500"}`}
                    onClick={() => setPolicyForm((current) => {
                      const days = new Set(current.weekly_off_days || []);
                      if (days.has(dayIndex)) days.delete(dayIndex);
                      else days.add(dayIndex);
                      return { ...current, weekly_off_days: Array.from(days).sort((a, b) => a - b) };
                    })}
                    data-testid={`settings-weekly-off-${label.toLowerCase()}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
          <Button type="button" className="rounded-2xl bg-[#F4A300] text-[#0D1B2A] hover:bg-[#D89000]" onClick={() => handleSettingsAction("policy")} data-testid="settings-policy-save-button">Save policy</Button>
        </CardContent>
      </Card>

      <div className="grid gap-6 xl:grid-cols-3">
        <Card className="border-slate-200/70 bg-white/85" data-testid="settings-location-card">
          <CardHeader><CardTitle data-testid="settings-location-title">Locations</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 md:grid-cols-[1fr_auto]">
              <Input value={settingsForms.locationName} onChange={(event) => setSettingsForms((current) => ({ ...current, locationName: event.target.value }))} placeholder="New location" data-testid="settings-location-input" />
              <Button type="button" className="rounded-2xl bg-[#0D1B2A] text-white hover:bg-[#08131F]" onClick={handleCreateLocation} data-testid="settings-location-save-button">Add</Button>
            </div>
            <div className="space-y-3">
              {(settingsData?.locations || []).map((location) => (
                <div key={location.id} className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-3" data-testid={`settings-location-item-${location.id}`}>
                  <span className="text-sm font-semibold text-slate-900">{location.name}</span>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" className="rounded-2xl" onClick={() => handleRenameLocation(location)} data-testid={`settings-location-rename-${location.id}`}>Rename</Button>
                    <Button type="button" variant="outline" className="rounded-2xl" onClick={() => handleDeleteLocation(location)} data-testid={`settings-location-delete-${location.id}`}>Delete</Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200/70 bg-white/85" data-testid="settings-business-unit-card">
          <CardHeader><CardTitle data-testid="settings-business-unit-title">Business Units</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 md:grid-cols-[1fr_auto]">
              <Input value={settingsForms.businessUnitName} onChange={(event) => setSettingsForms((current) => ({ ...current, businessUnitName: event.target.value }))} placeholder="New business unit" data-testid="settings-business-unit-input" />
              <Button type="button" className="rounded-2xl bg-[#0D1B2A] text-white hover:bg-[#08131F]" onClick={handleCreateBusinessUnit} data-testid="settings-business-unit-save-button">Add</Button>
            </div>
            <div className="space-y-3">
              {(settingsData?.business_units || []).map((businessUnit) => (
                <div key={businessUnit.id} className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-3" data-testid={`settings-business-unit-item-${businessUnit.id}`}>
                  <span className="text-sm font-semibold text-slate-900">{businessUnit.name}</span>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" className="rounded-2xl" onClick={() => handleRenameBusinessUnit(businessUnit)} data-testid={`settings-business-unit-rename-${businessUnit.id}`}>Rename</Button>
                    <Button type="button" variant="outline" className="rounded-2xl" onClick={() => handleDeleteBusinessUnit(businessUnit)} data-testid={`settings-business-unit-delete-${businessUnit.id}`}>Delete</Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200/70 bg-white/85" data-testid="settings-department-card">
          <CardHeader><CardTitle data-testid="settings-department-title">Departments & teams</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 md:grid-cols-[1fr_auto]">
              <Input value={settingsForms.departmentName} onChange={(event) => setSettingsForms((current) => ({ ...current, departmentName: event.target.value }))} placeholder="New department" data-testid="settings-department-input" />
              <Button type="button" className="rounded-2xl bg-[#0D1B2A] text-white hover:bg-[#08131F]" onClick={() => handleSettingsAction("department")} data-testid="settings-department-save-button">Add</Button>
            </div>
            <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto]">
              <Input value={settingsForms.teamName} onChange={(event) => setSettingsForms((current) => ({ ...current, teamName: event.target.value }))} placeholder="New team" data-testid="settings-team-input" />
              <select className="h-11 rounded-2xl border border-slate-200 px-3 text-sm" value={settingsForms.teamDepartmentId} onChange={(event) => setSettingsForms((current) => ({ ...current, teamDepartmentId: event.target.value }))} data-testid="settings-team-department-select">
                {(settingsData?.departments || []).map((department) => <option key={department.id} value={department.id}>{department.name}</option>)}
              </select>
              <Button type="button" className="rounded-2xl bg-[#0D1B2A] text-white hover:bg-[#08131F]" onClick={() => handleSettingsAction("team")} data-testid="settings-team-save-button">Add</Button>
            </div>
            <div className="grid gap-3 md:grid-cols-2" data-testid="settings-department-lists">
              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
                <p className="font-semibold text-slate-900">Departments</p>
                <div className="mt-3 space-y-2">
                  {(settingsData?.departments || []).map((department) => <p key={department.id} className="text-sm text-slate-500" data-testid={`settings-department-item-${department.id}`}>{department.name}</p>)}
                </div>
              </div>
              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
                <p className="font-semibold text-slate-900">Teams</p>
                <div className="mt-3 space-y-2">
                  {(settingsData?.teams || []).map((team) => <p key={team.id} className="text-sm text-slate-500" data-testid={`settings-team-item-${team.id}`}>{team.name}</p>)}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200/70 bg-white/85" data-testid="settings-shift-card">
          <CardHeader><CardTitle data-testid="settings-shift-title">Shifts & holidays</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 md:grid-cols-2">
              <Input value={settingsForms.shiftName} onChange={(event) => setSettingsForms((current) => ({ ...current, shiftName: event.target.value }))} placeholder="Shift name" data-testid="settings-shift-name-input" />
              <select className="h-11 rounded-2xl border border-slate-200 px-3 text-sm" value={settingsForms.shiftType} onChange={(event) => setSettingsForms((current) => ({ ...current, shiftType: event.target.value }))} data-testid="settings-shift-type-select">
                <option value="fixed">Fixed</option>
                <option value="flexible">Flexible</option>
              </select>
              <Input type="time" value={settingsForms.shiftStart} onChange={(event) => setSettingsForms((current) => ({ ...current, shiftStart: event.target.value }))} data-testid="settings-shift-start-input" />
              <Input type="time" value={settingsForms.shiftEnd} onChange={(event) => setSettingsForms((current) => ({ ...current, shiftEnd: event.target.value }))} data-testid="settings-shift-end-input" />
              <Input type="number" value={settingsForms.shiftGrace} onChange={(event) => setSettingsForms((current) => ({ ...current, shiftGrace: event.target.value }))} placeholder="Grace minutes" data-testid="settings-shift-grace-input" />
              <Button type="button" className="rounded-2xl bg-[#0D1B2A] text-white hover:bg-[#08131F]" onClick={() => handleSettingsAction("shift")} data-testid="settings-shift-save-button">Add shift</Button>
            </div>
            <div className="grid gap-3 md:grid-cols-[1fr_180px_auto]">
              <Input value={settingsForms.holidayName} onChange={(event) => setSettingsForms((current) => ({ ...current, holidayName: event.target.value }))} placeholder="Holiday name" data-testid="settings-holiday-name-input" />
              <Input type="date" value={settingsForms.holidayDate} onChange={(event) => setSettingsForms((current) => ({ ...current, holidayDate: event.target.value }))} data-testid="settings-holiday-date-input" />
              <Button type="button" className="rounded-2xl bg-[#0D1B2A] text-white hover:bg-[#08131F]" onClick={() => handleSettingsAction("holiday")} data-testid="settings-holiday-save-button">Add holiday</Button>
            </div>
            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4" data-testid="settings-holiday-list-card">
              <p className="font-semibold text-slate-900">Holiday calendar</p>
              <div className="mt-3 grid gap-2">
                {(settingsData?.holidays || []).map((holiday) => (
                  <div key={holiday.id} className="flex items-center justify-between text-sm text-slate-500" data-testid={`settings-holiday-item-${holiday.id}`}>
                    <span>{holiday.name}</span>
                    <span>{formatDate(holiday.date)}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  return (
    <AppShell title={pageMeta[section].title} subtitle={pageMeta[section].subtitle} navItems={navItems} user={user} tenant={tenant} onLogout={logout}>
      {loading ? <Card className="border-slate-200/70 bg-white/85 p-10 text-center text-slate-500" data-testid="company-loading-card">Loading company workspace...</Card> : null}
      {!loading && section === "dashboard" ? renderDashboard() : null}
      {!loading && section === "employees" ? renderEmployees() : null}
      {!loading && section === "attendance" ? renderAttendance() : null}
      {!loading && section === "approvals" ? renderApprovals() : null}
      {!loading && section === "kiosk" ? renderKiosk() : null}
      {!loading && section === "leave-settings" ? renderLeaveSettings() : null}
      {!loading && section === "reports" ? renderReports() : null}
      {!loading && section === "settings" ? renderSettings() : null}
    </AppShell>
  );
}